/**
 * The shared safety core for every mutating tool's HITL bridge.
 *
 * The four mutating ops (symmetric/asymmetric margin, ring, boolean) each have
 * their own parse/quantize/resolve/preview/commit and their own proposal shape,
 * but the *gate* is identical: the ≤1-pending preamble, the resolve-failure →
 * recoverable mapping the model re-asks against, and the adopt-or-release
 * Apply/Reject wiring. Those drift-prone safety invariants live
 * here ONCE (and are exercised by all four ops' tests) rather than being
 * hand-copied per op. Each op file stays a thin, explicit function that calls
 * these helpers — pure and React-free.
 */
import type { AmbiguityReason, MutationOutcome } from '../../contract';
import { makeHandleGuard, type CommitResult } from './pipeline';

// Re-exported so every op file imports the single outcome from one place.
export type { MutationOutcome };

/** The model-forwarded tool call, identical for every op. */
export interface MutatingToolCall {
    readonly toolName: string;
    readonly toolCallId: string;
    readonly input: unknown;
}

/** Every op's deps carry the cross-op ≤1-pending signal. */
export interface PendingFlag {
    /** True if a mutating proposal is already awaiting Apply/Reject. */
    readonly pendingMutationExists: boolean;
}

/**
 * The plan a bridge returns, generic over the op-specific proposal `P`:
 * a previewed proposal for the card, or a recoverable outcome the model re-asks
 * against (no card, no mutation).
 */
export type MutatingPlan<P> =
    | {
          readonly kind: 'proposal';
          readonly toolCallId: string;
          readonly proposal: P;
      }
    | {
          readonly kind: 'recoverable';
          readonly toolCallId: string;
          readonly output: MutationOutcome;
      };

/** A model-facing ROI reference — the only fields a re-ask needs. */
interface RoiRef {
    readonly roiNumber: number;
    readonly name: string;
}

/** A resolve result, generic over the op's branded `Validated`. The op's
 *  native result is structurally assignable (richer candidates narrow here). */
export type ResolveResult<V> =
    | { readonly kind: 'validated'; readonly validated: V }
    | {
          readonly kind: 'ambiguous';
          readonly reason: AmbiguityReason;
          readonly candidates: ReadonlyArray<RoiRef>;
      }
    | {
          readonly kind: 'not-found';
          readonly suggestions: ReadonlyArray<RoiRef>;
      }
    | { readonly kind: 'invalid'; readonly reason: string };

/** Strip a candidate/suggestion to the {roiNumber, name} the model re-asks with. */
const toRef = (s: RoiRef): RoiRef => ({ roiNumber: s.roiNumber, name: s.name });

export function recoverable(
    toolCallId: string,
    output: MutationOutcome
): Extract<MutatingPlan<never>, { kind: 'recoverable' }> {
    return { kind: 'recoverable', toolCallId, output };
}

/** The `failed` recoverable outcome every stage (parse, resolve, preview)
 *  maps its errors through. */
export function failedPlan(
    toolCallId: string,
    reason: string
): Extract<MutatingPlan<never>, { kind: 'recoverable' }> {
    return recoverable(toolCallId, { status: 'failed', reason });
}

/** Narrow untyped model-supplied tool input to a record, or undefined. */
export function asRecord(input: unknown): Record<string, unknown> | undefined {
    return input !== null && typeof input === 'object'
        ? (input as Record<string, unknown>)
        : undefined;
}

/**
 * ≤1-pending preamble shared by every mutating op. Returns the early exit (the
 * recoverable "resolve the pending one first" outcome the server gate also
 * enforces — defence-in-depth), or `'ok'` to proceed.
 */
export function mutatingPreamble(
    call: MutatingToolCall,
    deps: PendingFlag
): MutatingPlan<never> | 'ok' {
    if (deps.pendingMutationExists) {
        return failedPlan(
            call.toolCallId,
            'a previous proposal is still awaiting your Apply or Reject; ' +
                'resolve it before proposing another'
        );
    }
    return 'ok';
}

/**
 * Map a resolve failure to the recoverable plan the model re-asks against,
 * stripping candidates/suggestions to the model-facing ref. The caller narrows
 * `validated` itself (`if (resolved.kind !== 'validated') return …`).
 */
export function resolveFailureToPlan(
    resolved: Exclude<ResolveResult<never>, { kind: 'validated' }>,
    toolCallId: string
): Extract<MutatingPlan<never>, { kind: 'recoverable' }> {
    switch (resolved.kind) {
        case 'ambiguous':
            return recoverable(toolCallId, {
                status: 'ambiguous',
                reason: resolved.reason,
                candidates: resolved.candidates.map(toRef),
            });
        case 'not-found':
            return recoverable(toolCallId, {
                status: 'not-found',
                suggestions: resolved.suggestions.map(toRef),
            });
        case 'invalid':
            return failedPlan(toolCallId, resolved.reason);
    }
}

/**
 * The gate behaviors every proposal carries, identical across ops: hover
 * preview of the already-computed geometry, and the adopt-or-release
 * Apply/Reject. `apply()` claims the borrowed handle, commits, and maps the
 * result to the shared outcome; `reject()` frees the unclaimed handle (via the
 * shared {@link makeHandleGuard}). Returns the four fields a proposal spreads in.
 */
export function gateProposal<C extends { readonly handle: unknown }>(
    computed: C,
    deps: {
        readonly preview?: { show(computed: C): void; hide(): void };
        readonly geometry: { releaseComputed?(handle: unknown): void };
        readonly commit?: { currentContextId?(): string | undefined };
    },
    commit: () => Promise<CommitResult>
): {
    showPreview(): void;
    hidePreview(): void;
    reject(): MutationOutcome;
    apply(): Promise<MutationOutcome>;
} {
    const guard = makeHandleGuard(computed, deps);
    // The structure-set identity this geometry was computed against, captured
    // at plan time. A switch before Apply leaves the eager texture in the old
    // set's frame, so refuse to commit it into the new set — free it instead.
    const plannedContextId = deps.commit?.currentContextId?.();
    return {
        showPreview: () => deps.preview?.show(computed),
        hidePreview: () => deps.preview?.hide(),
        reject: guard.reject,
        apply: async () => {
            if (deps.commit?.currentContextId?.() !== plannedContextId) {
                guard.reject();
                return {
                    status: 'failed',
                    reason:
                        'the active structure set changed before this was ' +
                        'applied; re-issue the request against the current set',
                };
            }
            guard.claim();
            const c = await commit();
            if (c.kind === 'failed') {
                return { status: 'failed', reason: c.reason };
            }
            return {
                status: 'applied',
                newRoiNumber: c.newRoiNumber,
                newRoi: c.newRoi,
                volumeCm3: c.volumeCm3,
                volumeDeltaCm3: c.volumeDeltaCm3,
            };
        },
    };
}
